import { useState } from "react";
import api from "@/lib/api";
import { ChevronDown, AlertCircle, CheckCircle2 } from "lucide-react";

// CC-365: one-time catch-up for sales counted in an approved CSR pool but never attached to that run. Owners + platform admins.
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const ACTION = { move_to_needs_pay_to_collect: "Move to Needs pay to collect", attach_run_reference: "Attach run reference only" };
const key = (r) => `${r.sale_id}|${r.run_id}`;

const paidOut = (r) => `${r.paid_outside?.paid_on || "yes"} · ${r.paid_outside?.reference || ""}`;
const DEFAULT_REASON = "Brett confirmed 10/29/2026: paid once via Kat's 10/12 list, not double paid.";

function Rows({ rows, testId, ticked, onTick, tickPrefix = "csr-catchup-tick", lastHdr, last }) {
  return (
    <table className="w-full text-[12px]" data-testid={testId}>
      <thead className="text-[10.5px] uppercase tracking-[.06em] text-[color:var(--cc-ink-muted)]">
        <tr>
          {onTick && <th className="py-1.5 px-2 w-8" />}
          <th className="py-1.5 px-2 text-left">Job #</th><th className="py-1.5 px-2 text-left">Run week</th>
          <th className="py-1.5 px-2 text-right">Counted</th><th className="py-1.5 px-2 text-left">Current stage</th>
          <th className="py-1.5 px-2 text-left">CSR</th><th className="py-1.5 px-2 text-left">{lastHdr}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={key(r)} className="border-t border-[#eef1f6]" data-testid={`${testId}-row-${r.job_number}`}>
            {onTick && (
              <td className="py-1.5 px-2">
                <input type="checkbox" checked={!!ticked[key(r)]} onChange={() => onTick(key(r))}
                       data-testid={`${tickPrefix}-${r.job_number}`} />
              </td>
            )}
            <td className="py-1.5 px-2 font-mono-data font-semibold">{r.job_number}</td>
            <td className="py-1.5 px-2">{r.pay_week_monday}</td>
            <td className="py-1.5 px-2 text-right font-mono-data">{money(r.csr_dollars_counted)}</td>
            <td className="py-1.5 px-2">{r.status}</td>
            <td className="py-1.5 px-2">{r.csr_name}</td>
            <td className="py-1.5 px-2">{last(r)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReviewForm({ reason, setReason, n, busy, onConfirm }) {
  if (reason === null) {
    return (
      <button type="button" onClick={() => setReason(DEFAULT_REASON)} disabled={busy || !n} data-testid="csr-catchup-review-btn"
              className="mt-2 px-3 py-1.5 text-[12.5px] font-semibold rounded-lg border border-[#cfd4dc] disabled:opacity-50">
        Mark reviewed ({n})
      </button>
    );
  }
  const short = reason.trim().length < 20;
  return (
    <div className="mt-2 space-y-1.5" data-testid="csr-catchup-review-form">
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="csr-catchup-review-reason"
                className="w-full border border-[#cfd4dc] rounded px-2 py-1 text-[12px]" />
      <div className="flex gap-2 items-center">
        <button type="button" onClick={onConfirm} disabled={busy || !n || short} data-testid="csr-catchup-review-confirm"
                className="px-3 py-1.5 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white disabled:opacity-50">
          Confirm mark reviewed ({n})
        </button>
        <button type="button" onClick={() => setReason(null)} data-testid="csr-catchup-review-cancel"
                className="text-[12px] text-[color:var(--cc-ink-muted)]">Cancel</button>
        {short && <span className="text-[11px] text-[#b42318]" data-testid="csr-catchup-review-short">Reason needs at least 20 characters.</span>}
      </div>
    </div>
  );
}

export const ApprovedPoolCatchup = ({ slug, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const [rvTicked, setRvTicked] = useState({});
  const [reason, setReason] = useState(null);
  const [showReviewed, setShowReviewed] = useState(false);
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data: d } = await api.get(`/csr-sales/approved-pool-catchup?tenant_slug=${slug}`);
      setData(d); setTicked({}); setRvTicked({});
    } catch (e) { setErr(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const markReviewed = async () => {
    const items = (data?.review_paid_outside || []).filter((r) => rvTicked[key(r)]).map((r) => ({ sale_id: r.sale_id, run_id: r.run_id }));
    setBusy(true); setErr(null);
    try {
      await api.post(`/csr-sales/approved-pool-catchup/review?tenant_slug=${slug}`, { items, reason });
      setReason(null);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : d?.message || e.message); setBusy(false);
    }
  };
  const nRv = Object.values(rvTicked).filter(Boolean).length;
  const toggle = () => { if (!open && !data) load(); setOpen(!open); };
  const apply = async () => {
    const items = (data?.apply || []).filter((r) => ticked[key(r)]).map((r) => ({ sale_id: r.sale_id, run_id: r.run_id }));
    setBusy(true); setErr(null);
    try {
      const { data: res } = await api.post(`/csr-sales/approved-pool-catchup/apply?tenant_slug=${slug}`, { items });
      setDone(res.applied); onApplied?.();
      const { data: d } = await api.get(`/csr-sales/approved-pool-catchup?tenant_slug=${slug}`);
      setData(d); setTicked({});
    } catch (e) {
      const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : d?.message || e.message);
    } finally { setBusy(false); }
  };
  const nTicked = Object.values(ticked).filter(Boolean).length;
  return (
    <section className="bg-white border border-[#e7e9ee] rounded-xl" data-testid="csr-catchup-panel">
      <button type="button" onClick={toggle} data-testid="csr-catchup-toggle"
              className="w-full flex items-center gap-2 px-5 py-3 text-left font-semibold text-[14px]">
        <ChevronDown size={14} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
        Approved-pool catch-up
        <span className="ml-2 text-[11.5px] font-normal text-[color:var(--cc-ink-muted)]">
          Sales counted in an approved CSR pool that were never moved forward. Owners and platform admins only.
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 space-y-4">
          {err && <div className="text-[12px] text-[#b42318] flex items-center gap-1" data-testid="csr-catchup-error"><AlertCircle size={12} /> {String(err)}</div>}
          {done && (
            <div className="text-[12px] text-[#0e6b52] flex items-center gap-1" data-testid="csr-catchup-applied">
              <CheckCircle2 size={12} /> Applied {done.length}: {done.map((d) => d.job_number).join(", ") || "none"}
            </div>
          )}
          {!data ? <div className="text-[12px] text-[color:var(--cc-ink-muted)]">Loading…</div> : (
            <>
              <div>
                <div className="text-[12.5px] font-semibold mb-1">Ready to apply · {data.apply.length}</div>
                {data.apply.length ? (
                  <Rows rows={data.apply} testId="csr-catchup-apply" ticked={ticked}
                        onTick={(k) => setTicked({ ...ticked, [k]: !ticked[k] })} lastHdr="Change" last={(r) => ACTION[r.action]} />
                ) : <div className="text-[12px] text-[color:var(--cc-ink-muted)]" data-testid="csr-catchup-apply-empty">Nothing to apply.</div>}
                <button type="button" onClick={apply} disabled={busy || !nTicked} data-testid="csr-catchup-apply-btn"
                        className="mt-2 px-3 py-1.5 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white disabled:opacity-50">
                  Apply {nTicked} ticked
                </button>
              </div>
              <div>
                <div className="text-[12.5px] font-semibold mb-1">Review only: paid outside the app · {data.review_paid_outside.length}</div>
                <p className="text-[11.5px] text-[color:var(--cc-ink-muted)] mb-1">
                  Counted in an approved pool and also recorded as paid outside the app. Never changed automatically.
                </p>
                {data.review_paid_outside.length > 0 && (
                  <Rows rows={data.review_paid_outside} testId="csr-catchup-review" ticked={rvTicked} tickPrefix="csr-catchup-review-tick"
                        onTick={(k) => setRvTicked({ ...rvTicked, [k]: !rvTicked[k] })} lastHdr="Paid outside" last={paidOut} />
                )}
                <ReviewForm reason={reason} setReason={setReason} n={nRv} busy={busy} onConfirm={markReviewed} />
                {data.reviewed_paid_outside?.length > 0 && (
                  <div className="mt-2">
                    <button type="button" onClick={() => setShowReviewed(!showReviewed)} data-testid="csr-catchup-show-reviewed"
                            className="text-[12px] font-semibold text-[#0072bc]">
                      {showReviewed ? "Hide" : "Show"} reviewed ({data.reviewed_paid_outside.length})
                    </button>
                    {showReviewed && (
                      <Rows rows={data.reviewed_paid_outside} testId="csr-catchup-reviewed" lastHdr="Reviewed"
                            last={(r) => `${r.review?.reason} — ${r.review?.reviewer_email}, ${String(r.review?.reviewed_at || "").slice(0, 10)}`} />
                    )}
                  </div>
                )}
              </div>
              {data.counted_without_sale.length > 0 && (
                <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]" data-testid="csr-catchup-no-sale">
                  Counted with no CSR Sales Log row: {data.counted_without_sale.map((r) => `${r.job_number} (${r.pay_week_monday})`).join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};
